// NotificationsManagement: In-app + email, audit trail
import React, { useEffect, useState } from 'react';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from '@mui/material';
import api from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

function NotificationsManagement() {
  const { notifications, refresh } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [logOpen, setLogOpen] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState({
    type: '',
    user: '',
    content: '',
    relatedLink: '',
    status: 'new',
    sendEmail: false,
    email: ''
  });
  const navigate = useNavigate();

  // Fetch notifications on mount and poll for new activities every 30s
  useEffect(() => {
    // Always refresh notifications on mount
    handleRefresh();
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (id) => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/notifications/${id}`);
      await refresh();
      setSnackbar({ open: true, message: 'Notification deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewLog = async (notificationId) => {
    setLogLoading(true);
    setLogOpen(true);
    try {
      const res = await api.get(`/api/notifications/${notificationId}/logs`);
      setLogData(res.data);
    } catch {
      setLogData([]);
    } finally {
      setLogLoading(false);
    }
  };

  // Add refresh function
  // Fetch notifications, including new bookings and activities
  const handleRefresh = () => {
    setLoading(true);
    // Use provider refresh to update global notifications
    refresh().catch(() => {});
    setLoading(false);
  };

  // Add notification modal logic
  const handleAddNotification = () => {
    setModalOpen(true);
    setModalData({
      type: '',
      user: '',
      content: '',
      relatedLink: '',
      status: 'new',
      sendEmail: false,
      email: ''
    });
  };

  const handleModalChange = (e) => {
    setModalData({ ...modalData, [e.target.name]: e.target.value });
  };

  const handleModalCheckbox = (e) => {
    setModalData({ ...modalData, sendEmail: e.target.checked });
  };

  const handleModalSend = async () => {
    setModalLoading(true);
    try {
      // Send notification
      await api.post('/api/notifications', modalData);

      // If sendEmail is checked, send email to booker
      if (modalData.sendEmail && modalData.email) {
        await api.post('/api/notifications/send-email', {
          email: modalData.email,
          type: modalData.type,
          content: modalData.content,
          template: modalData.type // e.g. 'payment', 'booking', etc.
        });
      }

      setSnackbar({ open: true, message: 'Notification sent!', severity: 'success' });
      setModalOpen(false);
      handleRefresh();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Send failed', severity: 'error' });
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4, px: { xs: 1, md: 0 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Notifications & Logs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNotification}
          >
            Add Notification
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {/* Notification Modal */}
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Notification</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Type"
              name="type"
              value={modalData.type}
              onChange={handleModalChange}
              fullWidth
              margin="normal"
              required
            >
              <MenuItem value="booking">Booking</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <TextField
              label="User/Recipient"
              name="user"
              value={modalData.user}
              onChange={handleModalChange}
              fullWidth
              margin="normal"
              required
              placeholder="Enter staff/admin username or email"
            />
            <TextField
              label="Content"
              name="content"
              value={modalData.content}
              onChange={handleModalChange}
              fullWidth
              margin="normal"
              required
              multiline
              minRows={2}
              placeholder="Notification message"
            />
            <TextField
              label="Related Link"
              name="relatedLink"
              value={modalData.relatedLink}
              onChange={handleModalChange}
              fullWidth
              margin="normal"
              placeholder="Optional: link to booking, payment, etc."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={modalData.sendEmail}
                  onChange={handleModalCheckbox}
                  name="sendEmail"
                  color="primary"
                />
              }
              label="Send as email to booker"
            />
            {modalData.sendEmail && (
              <TextField
                label="Booker Email"
                name="email"
                value={modalData.email}
                onChange={handleModalChange}
                fullWidth
                margin="normal"
                required
                placeholder="Enter booker's email address"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)} disabled={modalLoading}>Cancel</Button>
            <Button
              onClick={handleModalSend}
              variant="contained"
              color="primary"
              disabled={
                modalLoading ||
                !modalData.type ||
                !modalData.user ||
                !modalData.content ||
                (modalData.sendEmail && !modalData.email)
              }
            >
              {modalLoading ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
        {loading ? <Typography>Loading...</Typography> : (
          <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Related Link</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((notification) => (
                <TableRow key={notification._id}>
                  <TableCell>{notification.user}</TableCell>
                  <TableCell>{notification.type}</TableCell>
                  <TableCell>{notification.content}</TableCell>
                  <TableCell>{notification.status}</TableCell>
                  <TableCell>
                    {notification.relatedLink && notification.relatedLink.trim() !== '' ? (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => {
                          // If link is internal, use navigate; else open in new tab
                          if (notification.relatedLink.startsWith('/')) {
                            navigate(notification.relatedLink);
                          } else {
                            window.open(notification.relatedLink, '_blank');
                          }
                        }}
                      >
                        View
                      </Button>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{notification.sentAt}</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => handleViewLog(notification._id)}>View Log</Button>
                    <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => handleDelete(notification._id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Dialog open={logOpen} onClose={() => setLogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Notification Log</DialogTitle>
          <DialogContent>
            {logLoading ? <Typography>Loading...</Typography> : (
              logData.length === 0 ? <Typography color="text.secondary">No logs found.</Typography> : (
                <Box>
                  {logData.map((log, i) => (
                    <Box key={i} sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight="bold">{log.actionType || log.action}</Typography>
                      <Typography variant="body2">{log.description || log.message}</Typography>
                      {log.userId && <Typography variant="caption" color="text.secondary">User: {log.userId.name || log.userId.email || log.userId}</Typography>}
                      <Typography variant="caption" color="text.secondary">{log.createdAt ? new Date(log.createdAt).toLocaleString() : (log.date || '')}</Typography>
                    </Box>
                  ))}
                </Box>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default NotificationsManagement;
